import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance, initializeMsal } from '@/auth/msalConfig';
import { App } from './App';
import { loadStatusPalette } from '@/lib/statusPalette';
import './index.css';

void (async () => {
    // Status colours come from the DB (migration 135). Kicked off in parallel
    // with MSAL and deliberately NOT awaited: index.css ships the same values
    // as defaults, so the app paints correctly either way and never waits on a
    // config call to show a calendar.
    void loadStatusPalette();

    // MSAL FAILING MUST NOT BLANK A WALL DISPLAY.
    //
    // This app serves three surfaces that use no Entra identity at all — the
    // dockview board, the labour-adjacent kiosks and the magic-link carrier
    // portal — and it used to render NOTHING until initializeMsal() resolved.
    // So any MSAL failure took out a TV on a warehouse wall that never needed
    // auth in the first place. That is not hypothetical: MSAL needs a secure
    // context for Web Crypto, and over plain HTTP it throws `crypto_nonexistent`
    // and the whole page stays empty.
    //
    // Rendering anyway is safe. MsalProvider tolerates an uninitialised
    // instance, and the surfaces that DO need a login discover that through the
    // normal signed-out path — a sign-in prompt is a far better failure than a
    // black screen. The error is logged rather than swallowed: an admin who
    // cannot log in deserves a console message that says why.
    try {
        await initializeMsal();
    } catch (err) {
        console.error(
            '[startup] MSAL failed to initialise — rendering anyway so the public '
            + 'surfaces (dockview board, carrier portal) still work. Signing in '
            + 'will not work until this is resolved.',
            err,
        );
    }

    const root = createRoot(document.getElementById('root')!);

    root.render(
        <StrictMode>
            <MsalProvider instance={msalInstance}>
                <App />
            </MsalProvider>
        </StrictMode>,
    );
})();
